/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Box, Grid } from '@mui/material';
import {
  UpdateChangeDtoStatusEnum,
  UpdateWorkPackageDtoStatusEnum,
  GetChangeDto,
  GetWorkPackageDto,
  ProcessListForChangeDto,
  UpdateChangeDto,
  CreatorRole,
} from '@codefluegel/zeta-change-typescript-client';

/**
 * The internal dependencies
 */
import { useNavigate, useParams } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  useChangeApiFactory,
  useProcessApiFactory,
  useWorkMaterialApiFactory,
  useWorkPackageApiFactory,
  useWorkServiceApiFactory,
} from '../ApiClient';
import ProcessAccordionItem from '../components/ProcessItems/ProcessAccordionItem';
import { ActiveProcessType } from '../types/processes';
import WorkPackageModal from '../components/Dialog/WorkPackageModal';
import { useChangeStore } from '../store/changeStore';
import WorkPackageViewModal from '../components/Dialog/WorkPackageViewModal';
import LoadSpinner from '../components/LoadSpinner/LoadSpinner';
import useLoading from '../utils/useLoading';
import { NotificationTypes } from '../types/notification';
import NotificationMessages from '../constants/notification';
import { useStorex } from '../store/UIStore';
import {
  checkIfRejected,
  useUserHasFullAccess,
} from '../utils/permissionHelper';
import ButtonsGroup from '../components/ButtonsGroup/ButtonsGroup';
import { DialogType } from '../types/changes';
import ChangeStatusDialogWrapper from '../components/Dialog/ChangeStatusDialogWrapper';
import ServiceModal from '../components/Dialog/ServiceModal';
import MaterialModal from '../components/Dialog/MaterialModal';
import TodoCard from '../components/TodoCard/TodoCard';
import NumbersPanel from '../components/Panel/NumbersPanel';
import RejectionCard from '../components/TodoCard/RejectionCard';
import { getDepartmentById } from '../utils/utils';

export type ProcessFormType = {
  affected: boolean;
  timeImpact: boolean;
  timeImpactDescription: string;
  plannedMaterial: string;
};

const ProjectEditResourcesGroupPage = observer(() => {
  const { id } = useParams();
  const {
    change,
    reducedWorkPackages,
    isDataLoading,
    setUpdatedChange,
    pushNewWorkPackage,
    pushNewMaterialPackage,
    pushNewServicePackage,
    replaceUpdatedWorkPackage,
    removeWorkPackage,
    workPackages,
    setIsDataLoading,
    setWorkPackages,
    setWorkServices,
    setWorkMaterials,
    reducedServices,
    reducedWorkMaterials,
    removeServicePackage,
    replaceUpdatedServicePackage,
    removeMaterialPackage,
    replaceUpdatedMaterialPackage,
  } = useChangeStore();
  const { changeId, processId } = useParams();
  const navigate = useNavigate();
  const { setNotification } = useStorex();
  const NotificationMessage = NotificationMessages();

  const auth = useAuth();

  const isRejected = checkIfRejected(change);

  const changeApi = useChangeApiFactory();
  const processApi = useProcessApiFactory();
  const workPackageApi = useWorkPackageApiFactory();

  const workServicesApi = useWorkServiceApiFactory();
  const workMaterialsApi = useWorkMaterialApiFactory();

  const { isLoading, toggleIsLoading } = useLoading();
  const { t } = useTranslation();
  const [modalType, setModalType] = useState<DialogType | null>(null);

  let processes = change?.processes || [];

  const showButtonGroup =
    change?.status === UpdateChangeDtoStatusEnum.InEvaluation ||
    change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
    change?.status ===
      UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress ||
    change?.status === UpdateChangeDtoStatusEnum.EvalComplete ||
    change?.status === UpdateChangeDtoStatusEnum.EvalRework;

  const findCurrentProcess = useCallback(
    (pId: number) => change?.processes.find(process => process.id === pId),
    [change],
  );

  const activeProcess = useLocalObservable<ActiveProcessType>(() => ({
    process: null,
    currentWorkPackage: null,
    setProcess: (currentProcess: ProcessListForChangeDto) => {
      if (currentProcess) {
        activeProcess.process = currentProcess;
        if (window.location.href.includes('resources'))
          navigate(
            `/change-request/${changeId}/resources/${currentProcess.id}`,
          );
        else {
          navigate(
            `/change-request/${changeId}/implementation/${currentProcess.id}`,
          );
        }
      }
    },
    resetProcess: () => {
      activeProcess.process = null;
      if (window.location.href.includes('resources'))
        navigate(`/change-request/${changeId}/resources`);
      else {
        navigate(`/change-request/${changeId}/implementation`);
      }
    },
    setCurrentWorkPackage: (workPackage?: GetWorkPackageDto) => {
      if (workPackage) {
        activeProcess.currentWorkPackage = workPackage;
      } else {
        activeProcess.currentWorkPackage = null;
      }
    },
  }));

  const [isWorpackageModalVisible, toggleWorpackageModalVisible] =
    useState(false);
  const [isWorpackageViewModalVisible, toggleWorpackageViewModalVisible] =
    useState(false);
  const [isServiceModalOpen, setServiceModalOpen] = useState(false);
  const [isMaterialModalOpen, setMaterialModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null,
  );
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(
    null,
  );

  // TODO Add temporary CTM and projektviewer
  const userHasFullAccess = useUserHasFullAccess(change);

  if (!userHasFullAccess) {
    processes =
      processes?.filter(process =>
        change?.commonProject?.navProcessToCommonProjectRelations.some(
          commonProcess =>
            commonProcess.id === process.pId &&
            (commonProcess.owner?.toLocaleLowerCase() ===
              auth.user?.profile.email?.toLocaleLowerCase() ||
              commonProcess.temporaryCoreTeamMember?.user.id.toLocaleLowerCase() ===
                auth.user?.profile.email?.toLocaleLowerCase()),
        ),
      ) ?? null;
  }

  const isCrWorkpackagesInProgress = !!(
    change &&
    (change.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
      change.status === UpdateChangeDtoStatusEnum.Check ||
      change.status === UpdateChangeDtoStatusEnum.Closed ||
      change.status === UpdateChangeDtoStatusEnum.Rejected ||
      change.status ===
        UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress)
  );
  const isImplementedBtnVisible = isCrWorkpackagesInProgress;

  const debouncedRequest = useDebouncedCallback(
    async (data: { [key: string]: string | boolean }) => {
      try {
        if (processId && activeProcess) {
          toggleIsLoading();

          const { data: updatedProcess } =
            await processApi.processesControllerUpdate({
              id: Number(processId),
              updateProcessDto: {
                affected: activeProcess.process?.affected,
                timeImpact: activeProcess.process?.timeImpact,
                timeImpactDescription:
                  activeProcess.process?.timeImpactDescription || '',
                plannedMaterial: activeProcess.process?.plannedMaterial || '',
                ...data,
              },
            });

          if (updatedProcess && change) {
            const updatedProcesses = change?.processes.map(process => {
              if (process.id === updatedProcess.id) {
                const updatedCurrnetProcess = {
                  ...process,
                  ...updatedProcess,
                };

                activeProcess.setProcess(updatedCurrnetProcess);
                return updatedCurrnetProcess;
              }
              return process;
            });

            const newChangeRequest: GetChangeDto | UpdateChangeDto = {
              ...change,
              processes: updatedProcesses,
            };

            setNotification({
              type: NotificationTypes.success,
              message: NotificationMessage.processUpdate,
            });
            setUpdatedChange(newChangeRequest);

            const allProcessesFinished = newChangeRequest?.processes.every(
              prcs => prcs.evaluationStatus === 'APPROVED',
            );

            if (allProcessesFinished) {
              await changeApi.changesControllerUpdate({
                id: change.id,
                updateChangeDto: {
                  ...change,
                  status: UpdateChangeDtoStatusEnum.EvalComplete,
                },
              });

              toggleIsLoading();
              navigate('/');
            }

            toggleIsLoading();
          }
        }
      } catch (e) {
        setNotification({
          type: NotificationTypes.error,
          message: NotificationMessage.error,
        });
        toggleIsLoading();
        console.error(e);
      }
    },
    300,
  );

  useEffect(() => {
    (async () => {
      if (changeId) {
        const [services, materials] = await Promise.all([
          workServicesApi.workServicesControllerFindAll({
            changeIds: [Number(changeId)],
          }),
          workMaterialsApi.workMaterialsControllerFindAll({
            changeIds: [Number(changeId)],
          }),
        ]);
        setWorkServices(services.data);
        setWorkMaterials(materials.data);
      }
    })();
  }, [
    changeId,
    workServicesApi,
    workMaterialsApi,
    setWorkServices,
    setWorkMaterials,
  ]);

  useEffect(() => {
    (async () => {
      if (changeId && change && processId) {
        const currentProcess = findCurrentProcess(Number(processId));
        if (currentProcess) activeProcess.setProcess(currentProcess);
      }
    })();
  }, [changeId, change, activeProcess, processId, findCurrentProcess]);

  const handleChange = useCallback(
    (key: string, value: string | boolean) => {
      debouncedRequest({ [key]: value });
    },
    [debouncedRequest],
  );

  const handleWorkPackageModal = useCallback(
    async (workPackageId?: number) => {
      try {
        if (workPackageId && !Number.isNaN(workPackageId)) {
          const { data: currentWorkPackage } =
            await workPackageApi.workPackagesControllerFindOne({
              id: workPackageId,
            });
          if (currentWorkPackage)
            activeProcess.setCurrentWorkPackage(currentWorkPackage);
        } else {
          activeProcess.setCurrentWorkPackage();
        }
      } catch (e) {
        console.warn(e);
      }

      if (isImplementedBtnVisible) {
        toggleWorpackageViewModalVisible(!isWorpackageViewModalVisible);
      } else {
        toggleWorpackageModalVisible(!isWorpackageModalVisible);
      }

      // check if workpackage complete
      if (
        change?.status === UpdateChangeDtoStatusEnum.CrWorkpackagesInProgress ||
        change?.status ===
          UpdateChangeDtoStatusEnum.CrWorkpackagesInInternalProgress
      ) {
        const { data: newWorkPackages } =
          await workPackageApi.workPackagesControllerFindAll({
            changeIds: [Number(changeId)],
          });

        const allWorkPackagesCompleted = newWorkPackages.every(
          wp => wp.status === 'IMPLEMENTED',
        );

        if (allWorkPackagesCompleted) {
          await changeApi.changesControllerUpdate({
            id: Number(changeId),
            updateChangeDto: {
              ...change,
              status: UpdateChangeDtoStatusEnum.Check,
            },
          });

          toggleIsLoading();
          navigate('/');
        }
      }
    },
    [
      isImplementedBtnVisible,
      workPackageApi,
      changeId,
      activeProcess,
      isWorpackageViewModalVisible,
      isWorpackageModalVisible,
      changeApi,
      change,
      toggleIsLoading,
      navigate,
    ],
  );

  const handleServiceModal = useCallback(
    (workServiceId?: number) => {
      setServiceModalOpen(!isServiceModalOpen);
      setSelectedServiceId(workServiceId ?? null);
    },
    [isServiceModalOpen],
  );

  const handleMaterialModal = useCallback(
    (workMaterialId?: number) => {
      setMaterialModalOpen(!isMaterialModalOpen);
      setSelectedMaterialId(workMaterialId ?? null);
    },
    [isMaterialModalOpen],
  );

  const handleProcessExpand = useCallback(
    (pId: number) => {
      if (pId === activeProcess.process?.id) {
        activeProcess.resetProcess();
      } else {
        const currentProcess = findCurrentProcess(Number(pId));
        if (currentProcess) activeProcess.setProcess(currentProcess);
      }
    },
    [activeProcess, findCurrentProcess],
  );

  const handleUpdateAllWorkPackages = useCallback(async () => {
    try {
      toggleIsLoading();
      const responses = reducedWorkPackages[Number(processId)].map(
        async workPackage => {
          const { data: updatedWorkPackage } =
            await workPackageApi.workPackagesControllerUpdate({
              id: workPackage.id,
              updateWorkPackageDto: {
                status: UpdateWorkPackageDtoStatusEnum.Implemented,
              },
            });
          replaceUpdatedWorkPackage({
            ...updatedWorkPackage,
            hasAttachments: workPackage.hasAttachments,
          });
        },
      );

      if (!!responses.length && changeId) {
        const arrResponses = await Promise.all(responses);
        if (arrResponses) {
          try {
            setIsDataLoading(true);
            const { data: newWorkPackages } =
              await workPackageApi.workPackagesControllerFindAll({
                changeIds: [Number(changeId)],
              });

            const allWorkPackagesCompleted = newWorkPackages.every(
              wp => wp.status === 'IMPLEMENTED',
            );

            if (allWorkPackagesCompleted) {
              await changeApi.changesControllerUpdate({
                id: Number(changeId),
                updateChangeDto: {
                  ...change,
                  status: UpdateChangeDtoStatusEnum.Check,
                },
              });

              toggleIsLoading();
              navigate('/');
            }

            if (workPackages) {
              setIsDataLoading(false);
              setWorkPackages(workPackages);
            }
          } catch (e) {
            setIsDataLoading(false);
            console.error(e);
          }

          toggleIsLoading();
          setNotification({
            type: NotificationTypes.success,
            message: NotificationMessage.workPackageAllStatusesUpdate,
          });
        }
      }
    } catch (e) {
      setNotification({
        type: NotificationTypes.error,
        message: NotificationMessage.error,
      });
      toggleIsLoading();
      console.error(e);
    }
  }, [
    toggleIsLoading,
    reducedWorkPackages,
    processId,
    changeId,
    workPackageApi,
    replaceUpdatedWorkPackage,
    setNotification,
    NotificationMessage.workPackageAllStatusesUpdate,
    NotificationMessage.error,
    setIsDataLoading,
    workPackages,
    changeApi,
    change,
    navigate,
    setWorkPackages,
  ]);

  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      gap={5}
      sx={{ marginTop: 2 }}
    >
      <Grid gap={0.5} size={8}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            marginBottom: 2,
          }}
        >
          <TodoCard
            text={
              change?.implementationTargetDate
                ? t('general.infoText.implementationDate', {
                    date: new Date(
                      change.implementationTargetDate,
                    ).toLocaleDateString(),
                  })
                : t('general.infoText.evaluateUntil', {
                    date: new Date(
                      change?.evaluationTargetDate || '',
                    ).toLocaleDateString(),
                  })
            }
          />

          {change?.rejectionComment &&
            (isRejected ||
              change?.status === UpdateChangeDtoStatusEnum.EvalRework) && (
              <RejectionCard text={change?.rejectionComment} />
            )}
          {isDataLoading && <LoadSpinner />}
          {!isDataLoading &&
            processes &&
            processes
              .slice()
              .sort((a, b) => {
                const aDepartment = getDepartmentById(
                  a.pId,
                  change?.commonProject?.navProcessToCommonProjectRelations,
                );
                const aGroupId = aDepartment?.groupNo ?? 0;

                const bDepartment = getDepartmentById(
                  b.pId,
                  change?.commonProject?.navProcessToCommonProjectRelations,
                );
                const bGroupId = bDepartment?.groupNo ?? 0;
                return aGroupId - bGroupId;
              })
              .map(process => (
                <ProcessAccordionItem
                  change={change}
                  changeId={id}
                  key={process.id}
                  process={process}
                  isExpanded={process.id.toString() === processId}
                  workPackages={reducedWorkPackages[process.id] || []}
                  workMaterials={reducedWorkMaterials[process.id] || []}
                  workServices={reducedServices[process.id] || []}
                  isImplementedBtnVisible={isImplementedBtnVisible}
                  isLoading={isLoading}
                  handleWorkPackageModal={handleWorkPackageModal}
                  handleChange={handleChange}
                  handleProcessExpand={handleProcessExpand}
                  handleUpdateAllWorkPackages={handleUpdateAllWorkPackages}
                  handleServiceModal={handleServiceModal}
                  handleMaterialModal={handleMaterialModal}
                />
              ))}
        </Box>
      </Grid>
      <Grid size={3}>
        {change && userHasFullAccess && <NumbersPanel change={change} />}

        {change && showButtonGroup && (
          <ButtonsGroup
            isChangeExist={!!change}
            changeStatus={change?.status || ''}
            workPackages={workPackages}
            change={change}
            onClickBtn={e =>
              setModalType(e.currentTarget.dataset.type as DialogType)
            }
          />
        )}
      </Grid>
      {isWorpackageModalVisible && (
        <WorkPackageModal
          change={change}
          currentProcess={activeProcess.process}
          currentWorkPackage={activeProcess.currentWorkPackage}
          isOpen={isWorpackageModalVisible}
          handleClose={handleWorkPackageModal}
          pushNewWorkPackage={pushNewWorkPackage}
          replaceUpdatedWorkPackage={replaceUpdatedWorkPackage}
          removeWorkPackage={removeWorkPackage}
          setNotification={setNotification}
        />
      )}
      <ChangeStatusDialogWrapper
        modalType={modalType}
        setModalType={m => setModalType(m)}
      />
      {isWorpackageViewModalVisible && activeProcess.currentWorkPackage && (
        <WorkPackageViewModal
          currentWorkPackage={activeProcess.currentWorkPackage}
          currentProcess={activeProcess.process}
          isOpen={isWorpackageViewModalVisible}
          handleClose={handleWorkPackageModal}
          replaceUpdatedWorkPackage={replaceUpdatedWorkPackage}
          setNotification={setNotification}
          change={change}
        />
      )}
      {change && activeProcess.process && (
        <>
          {isServiceModalOpen && (
            <ServiceModal
              isOpen={isServiceModalOpen}
              handleModal={() => {
                setServiceModalOpen(false);
                setSelectedServiceId(null);
              }}
              creatorRole={CreatorRole.CoreTeamMember}
              pushNewServicePackage={pushNewServicePackage}
              replaceUpdatedServicePackage={replaceUpdatedServicePackage}
              removeServicePackage={removeServicePackage}
              change={change}
              setNotification={setNotification}
              currentProcess={activeProcess.process}
              // Pass the selected service for editing
              selectedService={
                selectedServiceId != null
                  ? reducedServices[activeProcess.process.id]?.find(
                      s => s.id === selectedServiceId,
                    )
                  : undefined
              }
            />
          )}

          {isMaterialModalOpen && (
            <MaterialModal
              isOpen={isMaterialModalOpen}
              creatorRole={CreatorRole.CoreTeamMember}
              handleModal={() => {
                setMaterialModalOpen(false);
                setSelectedMaterialId(null);
              }}
              pushNewMaterialPackage={pushNewMaterialPackage}
              replaceUpdatedMaterialPackage={replaceUpdatedMaterialPackage}
              removeMaterialPackage={removeMaterialPackage}
              change={change}
              setNotification={setNotification}
              currentProcess={activeProcess.process}
              // Pass the selected material for editing
              selectedMaterial={
                selectedMaterialId != null
                  ? reducedWorkMaterials[activeProcess.process.id]?.find(
                      m => m.id === selectedMaterialId,
                    )
                  : undefined
              }
            />
          )}
        </>
      )}
    </Grid>
  );
});

export default ProjectEditResourcesGroupPage;
